import type { Types } from '@amplitude/analytics-browser'

// Last line of defence at the Amplitude egress. Whatever a call site builds, these keys never
// leave the browser: direct identifiers, user-typed or user-named text, and the auto-collected
// fields that carry them (the full page URL and title, download links).
// users.id stays the only person-level key; re-identification lives in the server DB.
const DROPPED_KEYS = new Set([
  'user_name',
  'email',
  'first_name',
  'last_name',
  'page_title',
  'query',
  'search_query',
  'brief_title',
  'group_name',
  '[Amplitude] Page URL',
  '[Amplitude] Page Location',
  '[Amplitude] Page Title',
  '[Amplitude] File Name',
  '[Amplitude] Link URL',
  '[Amplitude] Link Text',
])

// Query strings carry ids today, but a path is only safe to keep as its pathname.
const PATH_KEYS = new Set(['path', '[Amplitude] Page Path'])

const stripQuery = (value: unknown) =>
  typeof value === 'string' ? value.split('?')[0].split('#')[0] : value

export const scrubProperties = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(scrubProperties) as T
  if (value === null || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([key, nested]) => {
      if (DROPPED_KEYS.has(key)) return []
      if (PATH_KEYS.has(key)) return [[key, stripQuery(nested)]]
      return [[key, scrubProperties(nested)]]
    })
  ) as T
}

export const privacyPlugin: Types.EnrichmentPlugin = {
  name: 'linq-privacy',
  type: 'enrichment',
  execute: async (event) => {
    if (event.event_properties) event.event_properties = scrubProperties(event.event_properties)
    if (event.user_properties) event.user_properties = scrubProperties(event.user_properties)
    return event
  },
}
